import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, Alert, TextField, Button, List, ListItem, ListItemText, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import io from 'socket.io-client';

const socket = io(
  process.env.NODE_ENV === "production"
    ? "https://find.skyrool.com" // లేదా నీ backend actual domain
    : "http://localhost:4000",
  { transports: ["websocket"] }
);


function ChatPage() {
    const { conversationId } = useParams();
    const { token, currentUser } = useAuth();
    const [conversation, setConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const messagesEndRef = useRef(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [verifyModalOpen, setVerifyModalOpen] = useState(false);
    const [verificationQuestion, setVerificationQuestion] = useState('');

    useEffect(() => {
        if (!token) return;

        // Join conversation room
        socket.emit('joinConversation', conversationId);

        // Callback define outside .on
        const handleNewMessage = (newMessage) => {
            setMessages((prevMessages) => [...prevMessages, newMessage]);
        };

        socket.on('newMessage', handleNewMessage);

        const fetchInitialData = async () => {
            try {
                const convoRes = await axios.get('/api/messaging', { headers: { Authorization: `Bearer ${token}` } });
                const currentConvo = convoRes.data.find(c => c._id === conversationId);
                setConversation(currentConvo);

                const msgRes = await axios.get(`/api/messaging/${conversationId}/messages`, { headers: { Authorization: `Bearer ${token}` } });
                setMessages(msgRes.data);
            } catch (err) {
                setError('Failed to load chat details.');
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();

        // Cleanup listener properly
        return () => {
            socket.off('newMessage', handleNewMessage);
        };
    }, [token, conversationId]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(scrollToBottom, [messages]);

    const handleSendMessage = async (e, { isQuestion = false, isAnswer = false, text = newMessage } = {}) => {
        e.preventDefault();
        if (!text.trim()) return;
        try {
            await axios.post(`/api/messaging/${conversationId}/messages`, 
                { text, isVerificationQuestion: isQuestion, isVerificationAnswer: isAnswer },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setNewMessage('');
        } catch (err) {
            setError('Failed to send message.');
        }
    };

    const handleSendVerificationQuestion = (e) => {
        handleSendMessage(e, { isQuestion: true, text: verificationQuestion });
        setVerifyModalOpen(false);
        setVerificationQuestion('');
    };

    const handleMenuClick = (event, msg) => {
        setAnchorEl(event.currentTarget);
        setSelectedMessage(msg);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedMessage(null);
    };

    const handleDeleteMessage = async () => {
        try {
            await axios.delete(`/api/messaging/messages/${selectedMessage._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMessages(messages.filter(m => m._id !== selectedMessage._id));
        } catch (err) {
            setError("Failed to delete message.");
        }
        handleMenuClose();
    };

    const isFinder = conversation && conversation.itemDetails.foundBy === currentUser.id;

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '70vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" gutterBottom>Chat regarding "{conversation?.itemDetails?.itemName}"</Typography>
                {isFinder && (
                    <Button variant="outlined" startIcon={<HelpOutlineIcon />} onClick={() => setVerifyModalOpen(true)}>
                        Verify Ownership
                    </Button>
                )}
            </Box>
            <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2, border: '1px solid #ccc', p: 1, borderRadius: 1 }}>
                <List>
                    {messages.map(msg => (
                        <ListItem key={msg._id} sx={{ 
                            flexDirection: 'column',
                            alignItems: msg.senderId === currentUser.id ? 'flex-end' : 'flex-start'
                        }}>
                            <Paper sx={{ 
                                p: 1, 
                                maxWidth: '70%',
                                bgcolor: msg.isVerificationQuestion ? '#fffbe6' : (msg.senderId === currentUser.id ? 'primary.light' : 'grey.200'),
                                border: msg.isVerificationQuestion ? '1px solid #ffe58f' : 'none',
                            }}>
                                {msg.isVerificationQuestion && <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>Verification Question:</Typography>}
                                <ListItemText 
                                    primary={msg.text} 
                                    sx={{ fontStyle: msg.isDeleted ? 'italic' : 'normal', color: msg.isDeleted ? 'grey.600' : 'inherit' }} 
                                />
                                {msg.senderId === currentUser.id && !msg.isDeleted && (
                                    <IconButton size="small" onClick={(e) => handleMenuClick(e, msg)} sx={{ ml: 1, p: 0, float: 'right' }}>
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Paper>
                        </ListItem>
                    ))}
                    <div ref={messagesEndRef} />
                </List>
            </Box>
            <Box component="form" onSubmit={handleSendMessage} sx={{ display: 'flex', gap: 1 }}>
                <TextField fullWidth variant="outlined" size="small" placeholder="Type a message..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} />
                <Button type="submit" variant="contained">Send</Button>
            </Box>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleDeleteMessage}>Delete Message</MenuItem>
            </Menu>

            <Dialog open={verifyModalOpen} onClose={() => setVerifyModalOpen(false)} fullWidth>
                <DialogTitle>Ask a Verification Question</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Ask a question based on a secret identifying mark of the item. For example: "What is the color of the phone case?" or "What is written on the keychain?"
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={3}
                        label="Your Verification Question"
                        value={verificationQuestion}
                        onChange={(e) => setVerificationQuestion(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVerifyModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleSendVerificationQuestion} disabled={!verificationQuestion.trim()}>Send Question</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}

export default ChatPage;